import React, { useRef, useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './DemoVideoPlayer.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtTime = (s) => {
  if (!isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

// Resolve public-folder path correctly in both dev and Capacitor builds
const resolvePublicUrl = (path) => {
  if (!path) return path;
  // Already absolute URL — leave untouched
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const base = process.env.PUBLIC_URL || '';
  return `${base}${path}`;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const DemoVideoPlayer = ({ src, label = 'Watch Demo' }) => {
  const [open, setOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [error, setError] = useState(false);

  const videoRef  = useRef(null);
  const hideTimer = useRef(null);
  const resolvedSrc = resolvePublicUrl(src);

  const resetHideTimer = useCallback(() => {
    clearTimeout(hideTimer.current);
    setControlsVisible(true);
    if (playing) {
      hideTimer.current = setTimeout(() => setControlsVisible(false), 3000);
    }
  }, [playing]);

  useEffect(() => {
    if (!open) return;
    resetHideTimer();
    return () => clearTimeout(hideTimer.current);
  }, [open, playing, resetHideTimer]);

  // Pause & reset when modal closes
  useEffect(() => {
    if (!open && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setPlaying(false);
      setProgress(0);
      setCurrentTime(0);
      setError(false);
    }
  }, [open]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Keyboard: Space = play/pause, Esc = close, ← / → = seek
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape')     { setOpen(false); return; }
      if (e.key === ' ')          { e.preventDefault(); togglePlay(); }
      if (e.key === 'ArrowLeft')  seek(-10);
      if (e.key === 'ArrowRight') seek(10);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, playing]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); setPlaying(true); }
    else          { v.pause(); setPlaying(false); }
    resetHideTimer();
  };

  const seek = (delta) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min(v.duration, v.currentTime + delta));
    resetHideTimer();
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    setCurrentTime(v.currentTime);
    setProgress(v.currentTime / v.duration);
  };

  const handleScrub = (e) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x    = (e.touches?.[0]?.clientX ?? e.clientX) - rect.left;
    const frac = Math.max(0, Math.min(1, x / rect.width));
    v.currentTime = frac * v.duration;
    resetHideTimer();
  };

  const handleEnded = () => {
    setPlaying(false);
    setControlsVisible(true);
    clearTimeout(hideTimer.current);
  };

  if (!src) return null;

  // ── Modal content (rendered via portal to document.body) ──────────────────
  const modal = open ? (
    <div
      className="demo-overlay"
      onClick={() => setOpen(false)}
      onMouseMove={resetHideTimer}
      onTouchStart={resetHideTimer}
    >
      <div
        className="demo-modal"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >

        {/* Close */}
        <button className="demo-close" onClick={() => setOpen(false)} type="button">✕</button>

        {/* Video */}
        {error ? (
          <div className="demo-error">
            <p>⚠️ Unable to play this video.</p>
            <p style={{ fontSize: 13, opacity: 0.7 }}>
              Make sure the video is converted to MP4 (H.264 + AAC).
            </p>
          </div>
        ) : (
          <video
            ref={videoRef}
            className="demo-video"
            src={resolvedSrc}
            playsInline
            preload="metadata"
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={() => setDuration(videoRef.current?.duration || 0)}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={handleEnded}
            onError={() => setError(true)}
            onClick={togglePlay}
          />
        )}

        {/* Controls */}
        <div
          className={`demo-controls ${controlsVisible ? 'demo-controls-visible' : 'demo-controls-hidden'}`}
          onMouseMove={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="demo-scrubber" onClick={handleScrub} onTouchEnd={handleScrub}>
            <div className="demo-scrubber-track">
              <div className="demo-scrubber-fill" style={{ width: `${progress * 100}%` }} />
              <div className="demo-scrubber-thumb" style={{ left: `${progress * 100}%` }} />
            </div>
          </div>

          <div className="demo-btn-row">
            <div className="demo-btn-group">
              <button
                className="demo-ctrl-btn"
                onPointerDown={(e) => { e.stopPropagation(); seek(-10); }}
                title="Back 10s"
                type="button"
              >
                <span className="demo-ctrl-icon">⟪</span>
                <span className="demo-ctrl-label">10</span>
              </button>

              <button
                className="demo-ctrl-btn demo-play-btn"
                onPointerDown={(e) => { e.stopPropagation(); togglePlay(); }}
                title={playing ? 'Pause' : 'Play'}
                type="button"
              >
                {playing ? '⏸' : '▶'}
              </button>

              <button
                className="demo-ctrl-btn"
                onPointerDown={(e) => { e.stopPropagation(); seek(10); }}
                title="Forward 10s"
                type="button"
              >
                <span className="demo-ctrl-icon">⟫</span>
                <span className="demo-ctrl-label">10</span>
              </button>
            </div>

            <span className="demo-time">
              {fmtTime(currentTime)} / {fmtTime(duration)}
            </span>
          </div>
        </div>

      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        className="demo-trigger"
        onClick={() => setOpen(true)}
        onTouchStart={(e) => { e.stopPropagation(); setOpen(true); }}
        type="button"
      >
        <span className="demo-trigger-icon">▶</span>
        <span className="demo-trigger-text">{label}</span>
      </button>

      {/* Portal: renders at document.body, bypasses any parent overflow/z-index */}
      {ReactDOM.createPortal(modal, document.body)}
    </>
  );
};

export default DemoVideoPlayer;
